import {
  Component,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  ViewChild,
  ElementRef,
  OnDestroy,
  OnInit,
  HostListener,
  Renderer2,
  ChangeDetectionStrategy,
  signal,
  computed,
  input
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { MarkdownModule } from 'ngx-markdown';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';
export type TooltipVariant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';

@Component({
  selector: 'app-tooltip',
  standalone: true,
  imports: [CommonModule, OverlayModule, MarkdownModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('tooltipAnimation', [
      state('void', style({
        opacity: 0,
        transform: 'scale(0.95) translate(var(--tooltip-transform, 0))'
      })),
      state('visible', style({
        opacity: 1,
        transform: 'scale(1) translate(0, 0)'
      })),
      transition('void => visible', [
        animate('150ms cubic-bezier(0.16, 1, 0.3, 1)')
      ]),
      transition('visible => void', [
        animate('100ms cubic-bezier(0.4, 0, 1, 1)')
      ])
    ])
  ],
  styles: [`
    :host ::ng-deep .markdown-content {
      p { margin-bottom: 0.75rem; }
      p:last-child { margin-bottom: 0; }
      ul, ol { 
        margin-bottom: 0.75rem; 
        padding-left: 1.25rem;
        list-style-position: outside;
      }
      ul { list-style-type: disc; }
      ol { list-style-type: decimal; }
      li { margin-bottom: 0.25rem; }
      li:last-child { margin-bottom: 0; }
      strong, b { font-weight: 700; color: inherit; }
      code { 
        background: rgba(255, 255, 255, 0.1); 
        padding: 0.125rem 0.25rem; 
        border-radius: 0.25rem; 
        font-family: monospace;
      }
    }
  `],
  template: `
    <div [class]="(flexCenter ? 'flex justify-center items-center w-full ' : (block ? 'block w-full ' : 'inline-block ')) + 'relative ' + (className || '')" #triggerContainer>
      <!-- Trigger Content -->
      <div 
        #trigger
        cdkOverlayOrigin
        #triggerOrigin="cdkOverlayOrigin"
        (mouseenter)="onMouseEnter()"
        (mouseleave)="onMouseLeave()"
        (focus)="onFocus()"
        (blur)="onBlur()"
        [attr.aria-describedby]="isVisible() ? tooltipId : null"
        [class]="(flexCenter ? 'flex justify-center items-center w-full ' : (block ? 'block w-full ' : 'inline-block ')) + 'cursor-default' + (triggerClassName || '')"
      >
        <ng-content></ng-content>
      </div>

      <!-- Strategy 1: Standard Absolute Tooltip (Safe, No Change) -->
      <div
        *ngIf="isVisible() && !useFixedStrategy"
        #tooltipElement
        [id]="tooltipId"
        [@tooltipAnimation]="isVisible() ? 'visible' : 'void'"
        role="tooltip"
        [class]="tooltipClasses()"
        [style]="tooltipStyles()"
      >
        <ng-container *ngTemplateOutlet="tooltipContent"></ng-container>
      </div>

      <!-- Strategy 2: CDK Overlay Tooltip (For Dropdowns/Scroll containers) -->
      <ng-template
        cdkConnectedOverlay
        [cdkConnectedOverlayOrigin]="triggerOrigin"
        [cdkConnectedOverlayOpen]="isVisible() && useFixedStrategy"
        [cdkConnectedOverlayPositions]="overlayPositions()"
        [cdkConnectedOverlayPanelClass]="overlayPanelClass()"
        [cdkConnectedOverlayPush]="true"
        (positionChange)="onPositionChange($event)"
      >
        <div
          #tooltipElement
          [id]="tooltipId"
          [@tooltipAnimation]="'visible'"
          role="tooltip"
          [class]="tooltipClasses() + ' !relative !top-0 !left-0 !transform-none'"
          [style.max-width]="maxWidth"
        >
          <ng-container *ngTemplateOutlet="tooltipContent"></ng-container>
        </div>
      </ng-template>

      <!-- Shared Tooltip Content Template -->
      <ng-template #tooltipContent>
        <div 
          [class.markdown-content]="isMarkdown"
          [class.max-h-[400px]]="isMarkdown"
          [class.overflow-y-auto]="isMarkdown"
          [class.px-1]="isMarkdown"
        >
          <ng-container *ngIf="!isMarkdown">{{ content }}</ng-container>
          <markdown *ngIf="isMarkdown" [data]="content"></markdown>
        </div>
        
        <ng-container *ngIf="customTemplate">
          <ng-container *ngTemplateOutlet="customTemplate"></ng-container>
        </ng-container>
        <div *ngIf="showArrow" [class]="arrowClasses()" [style]="arrowStyles()"></div>
      </ng-template>
    </div>
  `
})
export class TooltipComponent implements OnInit, OnDestroy {
  @ViewChild('trigger', { static: true }) triggerRef!: ElementRef;
  @ViewChild('tooltipElement') tooltipRef?: ElementRef;
  @ViewChild('triggerContainer', { static: true }) containerRef!: ElementRef;

  // Core Configuration
  @Input() content = '';
  @Input() customTemplate?: TemplateRef<any>;
  @Input() position: TooltipPosition = 'top';
  @Input() variant: TooltipVariant = 'default';
  @Input() showArrow = true;
  @Input() disabled = false;
  @Input() delayShow = 300;
  @Input() delayHide = 100;
  @Input() offset = 8;
  @Input() maxWidth = '320px';
  @Input() width?: string;
  
  // Styling Configuration
  @Input() className?: string;
  @Input() contentClassName?: string;
  @Input() triggerClassName?: string;
  @Input() customBackgroundColor?: string;
  @Input() customTextColor?: string;
  @Input() customArrowColor?: string;
  
  // Behavior Configuration
  @Input() trigger: 'hover' | 'focus' | 'click' | 'manual' = 'hover';
  @Input() closeOnClickOutside = true;
  @Input() closeOnEscape = true;
  @Input() block = false;
  @Input() flexCenter = false;
  @Input() useFixedStrategy = true;
  @Input() isMarkdown = false;
  readonly zIndex = input<number | null>(null);
  
  // Events
  @Output() visibilityChange = new EventEmitter<boolean>();
  @Output() show = new EventEmitter<void>();
  @Output() hide = new EventEmitter<void>();

  // Internal State
  isVisible = signal(false);
  tooltipId = `tooltip-${Math.random().toString(36).substr(2, 9)}`;
  private showTimeout?: ReturnType<typeof setTimeout>;
  private hideTimeout?: ReturnType<typeof setTimeout>;
  private computedPosition = signal<TooltipPosition>('top');

  // Computed Properties
  tooltipClasses = computed(() => {
    const baseClasses = [
      this.useFixedStrategy ? 'fixed' : 'absolute',
      'z-[1080] px-3 py-2 text-sm font-medium rounded-md shadow-lg',
      'pointer-events-none select-none w-max break-words whitespace-normal',
      this.useFixedStrategy ? '' : 'transition-opacity duration-150 leading-relaxed'
    ];

    const variantClasses = !this.customBackgroundColor ? this.getVariantClasses() : '';
    const positionClasses = this.getPositionClasses();
    const customClasses = this.className || '';
    const contentClasses = this.contentClassName || '';

    return [...baseClasses, variantClasses, positionClasses, customClasses, contentClasses]
      .filter(Boolean)
      .join(' ');
  });

  overlayPanelClass = computed(() => {
    const zIndex = this.zIndex();
    if (zIndex === 1) return 'app-tooltip-overlay-pane-z-1';
    if (zIndex === 99999) return 'app-tooltip-overlay-pane-z-max';
    return '';
  });

  tooltipStyles = computed(() => {
    const position = this.computedPosition();
    const styles: any = {
      maxWidth: this.maxWidth
    };

    if (this.zIndex() != null) {
      styles.zIndex = this.zIndex();
    }

    if (this.width) {
      styles.width = this.width;
      delete styles.maxWidth;
    }

    if (this.customBackgroundColor) {
      styles.backgroundColor = this.customBackgroundColor;
    }
    
    if (this.customTextColor) {
      styles.color = this.customTextColor;
    }

    if (!this.useFixedStrategy) {
      switch (position) {
        case 'top': styles['--tooltip-transform'] = '0, 4px'; break;
        case 'bottom': styles['--tooltip-transform'] = '0, -4px'; break;
        case 'left': styles['--tooltip-transform'] = '4px, 0'; break;
        case 'right': styles['--tooltip-transform'] = '-4px, 0'; break;
      }
    }

    return styles;
  });

  overlayPositions = computed<ConnectedPosition[]>(() => {
    const offset = this.offset || 8;
    return [
      { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -offset },
      { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: offset },
      { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offset },
      { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -offset }
    ];
  });

  arrowClasses = computed(() => {
    const position = this.computedPosition();
    const baseClasses = 'absolute w-2 h-2 rotate-45 z-[1080]';
    const variantClass = !this.customArrowColor ? this.getArrowVariantClass() : '';
    
    const positionClasses = {
      top: 'top-full left-1/2 -translate-x-1/2 -translate-y-1/2',
      bottom: 'bottom-full left-1/2 -translate-x-1/2 translate-y-1/2',
      left: 'left-full top-1/2 -translate-y-1/2 -translate-x-1/2',
      right: 'right-full top-1/2 -translate-y-1/2 translate-x-1/2'
    };

    return `${baseClasses} ${variantClass} ${positionClasses[position]}`;
  });

  arrowStyles = computed(() => {
    const styles: any = {};
    if (this.customArrowColor) {
      styles.backgroundColor = this.customArrowColor;
    } else if (this.customBackgroundColor) {
      styles.backgroundColor = this.customBackgroundColor;
    }
    return styles;
  });

  constructor(private renderer: Renderer2) {}

  ngOnInit(): void {
    this.setupEventListeners();
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.removeEventListeners();
  }

  // Event Handlers
  onMouseEnter(): void {
    if (this.trigger === 'hover' && !this.disabled) {
      this.showTooltip();
    }
  }

  onMouseLeave(): void {
    if (this.trigger === 'hover' && !this.disabled) {
      this.hideTooltip();
    }
  }

  onFocus(): void {
    if (this.trigger === 'focus' && !this.disabled) {
      this.showTooltip();
    }
  }

  onBlur(): void {
    if (this.trigger === 'focus' && !this.disabled) {
      this.hideTooltip();
    }
  }

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    if (this.trigger === 'click' && !this.disabled) {
      event.stopPropagation();
      this.toggleTooltip();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.closeOnClickOutside && this.isVisible() && this.trigger === 'click') {
      const target = event.target as HTMLElement;
      if (!this.containerRef.nativeElement.contains(target)) {
        this.hideTooltip();
      }
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.closeOnEscape && this.isVisible()) {
      this.hideTooltip();
      event.preventDefault();
    }
  }

  onPositionChange(event: any): void {
    const pair = event.connectionPair;
    let pos: TooltipPosition = 'top';
    
    if (pair.originY === 'top' && pair.overlayY === 'bottom') pos = 'top';
    else if (pair.originY === 'bottom' && pair.overlayY === 'top') pos = 'bottom';
    else if (pair.originX === 'start' && pair.overlayX === 'end') pos = 'left';
    else if (pair.originX === 'end' && pair.overlayX === 'start') pos = 'right';
    
    this.computedPosition.set(pos);
  }

  // Public Methods
  showTooltip(): void {
    if (this.disabled || this.isVisible()) return;
    this.clearTimeouts();
    this.showTimeout = setTimeout(() => {
      this.updatePosition();
      this.isVisible.set(true);
      this.visibilityChange.emit(true);
      this.show.emit();
    }, this.delayShow);
  }

  hideTooltip(): void {
    this.clearTimeouts();
    this.hideTimeout = setTimeout(() => {
      this.isVisible.set(false);
      this.visibilityChange.emit(false);
      this.hide.emit();
    }, this.delayHide);
  }

  toggleTooltip(): void {
    if (this.isVisible()) {
      this.hideTooltip();
    } else {
      this.showTooltip();
    }
  }

  private updatePosition(): void {
    if (!this.triggerRef) return;

    if (!this.useFixedStrategy) {
      const triggerRect = this.triggerRef.nativeElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      
      let finalPosition = this.position;
      const spaceTop = triggerRect.top;
      const spaceBottom = viewportHeight - triggerRect.bottom;
      const spaceLeft = triggerRect.left;
      const spaceRight = viewportWidth - triggerRect.right;

      if (this.position === 'top' && spaceTop < 60 && spaceBottom > 60) finalPosition = 'bottom';
      else if (this.position === 'bottom' && spaceBottom < 60 && spaceTop > 60) finalPosition = 'top';
      else if (this.position === 'left' && spaceLeft < 100 && spaceRight > 100) finalPosition = 'right';
      else if (this.position === 'right' && spaceRight < 100 && spaceLeft > 100) finalPosition = 'left';

      this.computedPosition.set(finalPosition);
    } else {
      this.computedPosition.set(this.position);
    }
  }

  private getVariantClasses(): string {
    const variants = {
      default: 'bg-gray-900 text-white dark:bg-gray-700 dark:text-gray-100',
      primary: 'bg-purple-600 text-white dark:bg-purple-500',
      secondary: 'bg-gray-600 text-white dark:bg-gray-500',
      success: 'bg-green-600 text-white dark:bg-green-500',
      warning: 'bg-amber-600 text-white dark:bg-amber-500',
      danger: 'bg-red-600 text-white dark:bg-red-500',
      info: 'bg-blue-600 text-white dark:bg-blue-500'
    };
    return variants[this.variant] || variants.default;
  }

  private getArrowVariantClass(): string {
    const variants = {
      default: 'bg-gray-900 dark:bg-gray-700',
      primary: 'bg-purple-600 dark:bg-purple-500',
      secondary: 'bg-gray-600 dark:bg-gray-500',
      success: 'bg-green-600 dark:bg-green-500',
      warning: 'bg-amber-600 dark:bg-amber-500',
      danger: 'bg-red-600 dark:bg-red-500',
      info: 'bg-blue-600 dark:bg-blue-500'
    };
    return variants[this.variant] || variants.default;
  }

  private getPositionClasses(): string {
    if (this.useFixedStrategy) return '';
    const position = this.computedPosition();
    const positionClasses = {
      top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
      bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
      left: 'right-full top-1/2 -translate-y-1/2 mr-2',
      right: 'left-full top-1/2 -translate-y-1/2 ml-2'
    };
    return positionClasses[position];
  }

  private setupEventListeners(): void {}
  private removeEventListeners(): void {}

  private clearTimeouts(): void {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = undefined;
    }
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }
  }
}